import type { AchievementKey } from "./achievements.ts";

/**
 * The colours a peon's shirt can be (owner, 2026-09-24: peons are customisable, starting with the
 * shirt; hats stay off the table because the Peon of the Month wears the crown). "brown" is the art as
 * drawn; every other value names a recoloured copy of the sprite and its sheet
 * (scripts/make-shirts.mjs: public/ui/peon-<mood>-v1-<shirt>.png and the matching sheet) and of the
 * mascot, who is your peon in the account sheet (public/ui/peon-original-<shirt>.png and its @3x).
 */
export const SHIRTS = ["brown", "red", "blue", "green", "purple", "gold", "black"] as const;
export type Shirt = (typeof SHIRTS)[number];
export const DEFAULT_SHIRT: Shirt = "brown";

/** How a peon looks, as GET /api/peons/appearance returns it. */
export interface PeonAppearance {
  shirt: Shirt;
  hair: Hairstyle;
  /** Facial hair (shared/cosmetics.ts BEARDS); "none" as drawn. */
  beard: string;
  /** The accessories worn, at most one per slot (shared/cosmetics.ts normalizeAccessories); [] for none. */
  accessories: string[];
  /** The badges pinned on the shirt, in the order chosen, at most three (shared/achievements.ts normalizeWornBadges); [] for none. */
  badges: AchievementKey[];
}

export function isShirt(value: unknown): value is Shirt {
  return typeof value === "string" && (SHIRTS as readonly string[]).includes(value);
}

/** What the picker calls each one. */
export const SHIRT_LABELS: Record<Shirt, string> = {
  brown: "Brown",
  red: "Red",
  blue: "Blue",
  green: "Green",
  purple: "Purple",
  gold: "Gold",
  black: "Black",
};

/** The swatch colour shown in the picker: each shirt's mid tone. */
export const SHIRT_SWATCH: Record<Shirt, string> = {
  brown: "#8b5839",
  red: "#b8301f",
  blue: "#2f6fa8",
  green: "#3f7d3a",
  purple: "#5b2f8c",
  gold: "#d9a21b",
  black: "#382a1f",
};

/**
 * The peon's hair (owner, 2026-10-04: "You should be able to choose a hairstyle for your peon").
 * Like the shirt, a per-peon choice made in the account sheet and worn on your peon (the mascot),
 * the crowned Peon of the Month portrait and the Hall of Fame. Hair sits under the crown, which is
 * why hats stay ruled out. "none" is the mascot as drawn: bald. Every other style is a transparent
 * 46x44 layer drawn by scripts/make-hair.mjs, a plain one and a crowned one with the crown's pixels
 * cleared, laid on top of the shirt art (see hairArt for the alignment).
 */
export const HAIRSTYLES = [
  "none",
  "mohawk",
  "topknot",
  "ponytail",
  "braid",
  "spiky",
  "mullet",
  // the second set (owner, 2026-10-04: "There should also be a set of female hairstyles")
  "long",
  "bob",
  "pigtails",
  "buns",
  "curls",
  "twinbraids",
  // owner, 2026-10-04: "think nascar trailer park woman, big poofy frizzy mullet like"
  "frizzmullet",
] as const;
export type Hairstyle = (typeof HAIRSTYLES)[number];
export const DEFAULT_HAIR: Hairstyle = "none";

export function isHairstyle(value: unknown): value is Hairstyle {
  return typeof value === "string" && (HAIRSTYLES as readonly string[]).includes(value);
}

/** What the picker calls each one. */
export const HAIR_LABELS: Record<Hairstyle, string> = {
  none: "None",
  mohawk: "Mohawk",
  topknot: "Topknot",
  ponytail: "Ponytail",
  braid: "Braid",
  spiky: "Spiky",
  mullet: "Mullet",
  long: "Long",
  bob: "Bob",
  pigtails: "Pigtails",
  buns: "Buns",
  curls: "Curls",
  twinbraids: "Twin braids",
  frizzmullet: "Bossy Jossy",
};

/**
 * The public path of a hairstyle's 1x layer, or null for "none" (nothing to draw):
 * "/ui/hair-mohawk.png" for your peon, "/ui/hair-mohawk-crowned.png" for the crowned portrait.
 *
 * Both layers are 46x44, the crowned portrait's canvas (public/ui/peon-crowned*.png), so the hair can
 * stand above the head and the two share one coordinate system. Over the crowned portrait the layer
 * covers it exactly. Over the 46x38 mascot (public/ui/peon-original*.png) the mascot sits at rows 4
 * to 41 of the layer, as it does in peon-crowned.png: draw the layer 4 art pixels above the mascot's
 * top edge (it hangs 2 below its bottom edge, which are empty). In CSS px at the mascot's scale s
 * (box width / 46): top -4s, height 44s, same left and width. It is not bottom-aligned with the 38-row
 * mascot; that would put the hair 2 pixels low.
 *
 * Each has an @3x twin beside it, nearest-neighbour, written by scripts/make-hair.mjs. The clients
 * show the @3x files, as they do the shirt art (src/peon-art.ts userPeonArtwork, src/flags-core.ts
 * crownedPortraitUrl: "/ui/peon-original-red@3x.png"), so they take this path and swap the ".png"
 * ending for "@3x.png": "/ui/hair-mohawk@3x.png". The hair goes over the shirt image.
 */
export function hairArt(hair: Hairstyle, crowned: boolean): string | null {
  if (hair === "none") return null;
  return crowned ? `/ui/hair-${hair}-crowned.png` : `/ui/hair-${hair}.png`;
}
